import type { MouseEvent } from 'react'

import { Link } from '@/app/components/atoms/Link'
import { cx } from '@/app/lib/utils'

import styles from './SectionLink.module.scss'

/** Texto del enlace en móvil cuando el CMS no envía `link_title`. */
export const SECTION_LINK_MOBILE_LABEL = 'Ver todos'

export interface SectionLinkProps {
  readonly href: string
  /** `link_title` del CMS — se muestra en la cabecera (tablet+) y, si no está
   * vacío, también en el pie móvil. */
  readonly title: string
  readonly openInNewTab: boolean
  /** `header`: dentro de la cabecera, visible desde tablet con `title`.
   * `footer`: al final de la sección, centrado, visible solo en móvil con el
   * `title` del CMS, o `SECTION_LINK_MOBILE_LABEL` si viene vacío. */
  readonly placement: 'header' | 'footer'
  /** Clases extra: en `header`, del enlace (alineación propia de la cabecera);
   * en `footer`, de su contenedor (separación respecto al contenido). */
  readonly className?: string
  /** `false` desactiva el prefetch de `next/link` (enlaces dinámicos). */
  readonly prefetch?: boolean
  readonly onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
}

/**
 * Enlace "Ver todos" de las secciones de producto. Cada módulo lo pinta DOS
 * veces — `header` y `footer` — y el CSS deja visible solo una según el ancho
 * (nunca dos tabulables a la vez): móvil → pie centrado con el `link_title`
 * del CMS (o "Ver todos" si no hay); tablet+ → cabecera con el `link_title`.
 */
export function SectionLink({ href, title, openInNewTab, placement, className, prefetch, onClick }: SectionLinkProps) {
  const isFooter = placement === 'footer'
  const label = isFooter ? title.trim() || SECTION_LINK_MOBILE_LABEL : title
  const linkClass = isFooter ? styles.footerLink : cx(styles.headerLink, className)

  const link = openInNewTab ? (
    <Link href={href} external size="s" className={linkClass} onClick={onClick}>
      {label}
    </Link>
  ) : (
    <Link href={href} prefetch={prefetch} size="s" className={linkClass} onClick={onClick}>
      {label}
    </Link>
  )

  return isFooter ? <div className={cx(styles.footer, className)}>{link}</div> : link
}
